import React from 'react';
import { useSelector } from 'react-redux';
import { AlertDialog, ButtonGroup, Content, Dialog, DialogContainer, DialogTrigger, Heading, useDialogContainer, } from '@adobe/react-spectrum';
import { ActionButton, Button } from '@react-spectrum/button';
import { Divider } from '@react-spectrum/divider';
import { Flex } from '@react-spectrum/layout';
import { Text as SpectrumText } from '@react-spectrum/text';
import { View } from '@react-spectrum/view';
import Close from '@spectrum-icons/workflow/Close';
import FeedbackIcon from '@spectrum-icons/workflow/Feedback';
import ShareAndroid from '@spectrum-icons/workflow/ShareAndroid';
import ShareLight from '@spectrum-icons/workflow/ShareLight';
import { LogoInHTML } from 'app/assets/brands/Logo';
import { EditorActions } from 'app/stores/editor/actions';
import { ProjectActions } from 'app/stores/project/actions';
import { getPagesCount } from 'app/stores/selectors';
import { useAppDispatch } from 'app/stores/store';
import { PublishDialog } from 'app/views/components/dialogs/publish/PublishDialog';
import { Feedback } from 'app/views/components/header/feedback';
import { Share } from 'app/views/components/header/platform/share';
import { analytics } from 'app/utils/analytics';
import { hasClipboard } from 'app/utils/caniuse';
import { Capabilities } from 'app/utils/Capabilities';
import { saveToClipboard } from 'app/utils/clipboard';
import { saveToDevice } from 'app/utils/download';
const Consts = {
    clipboardEnabled: false,
};
function CropControls() {
    const dispatch = useAppDispatch();
    const onCancel = React.useCallback(() => dispatch(EditorActions.resetAppController()), [dispatch]);
    const onApply = React.useCallback(() => dispatch(EditorActions.applyImageCrop()), [dispatch]);
    return (React.createElement(Flex, { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, justifyContent: "center", alignItems: "center" },
        React.createElement(ButtonGroup, null,
            React.createElement(Button, { variant: "secondary", onPress: onCancel }, "Cancel"),
            React.createElement(Button, { variant: "cta", onPress: onApply }, "Apply"))));
}
const Clipboard = () => {
    const dispatch = useAppDispatch();
    const [showClipboard, setShowClipboard] = React.useState(false);
    const onClipboard = React.useCallback(() => {
        analytics({
            action: 'ExportAction',
            label: 'copyToClipboard',
        });
        dispatch(ProjectActions.rasterizeProject({
            format: 'image/png',
            strategy: 'current_page',
        }))
            .unwrap()
            .then(blob => saveToClipboard(blob))
            .then(() => setShowClipboard(true))
            .catch(() => {
            /* error info */
        });
    }, [dispatch]);
    // clipboard doesn't work on safari - research
    const clipboardEnabled = Consts.clipboardEnabled &&
        hasClipboard() &&
        !Capabilities.shared.isSafari;
    if (!clipboardEnabled) {
        return null;
    }
    return (React.createElement(ActionButton, { onPress: onClipboard, isQuiet: true },
        React.createElement(DialogContainer, { onDismiss: () => setShowClipboard(false) }, showClipboard && (React.createElement(AlertDialog, { variant: "information", title: "Image in clipboard", primaryActionLabel: "Done" },
            React.createElement(View, { marginBottom: "size-125" },
                React.createElement(SpectrumText, null, "Image has beed added to your clipboard")),
            React.createElement(SpectrumText, null, "You can now paste it in other places")))),
        "Copy to clipboard"));
};
const ShareDialog = () => {
    const dispatch = useAppDispatch();
    const { dismiss } = useDialogContainer();
    const onDownload = () => {
        analytics({
            action: 'ExportAction',
            label: 'download',
        });
        dispatch(ProjectActions.rasterizeProject({ format: 'image/jpeg' }))
            .unwrap()
            .then(blob => {
            if (Capabilities.shared.isSafari) {
                saveToDevice(blob, 'open_in_window');
            }
            else {
                saveToDevice(blob, 'open_in_tab');
            }
        });
    };
    const downloadLabel = Capabilities.shared.isAndroid
        ? 'Download on device'
        : 'Save to photos';
    return (React.createElement(Dialog, null,
        React.createElement(Heading, null, "Export image"),
        React.createElement(Content, null,
            React.createElement(Flex, { width: "100%", justifyContent: "center" },
                React.createElement(ButtonGroup, { orientation: "vertical", align: "center" },
                    React.createElement(Clipboard, null),
                    React.createElement(Share, null),
                    React.createElement(ActionButton, { onPress: onDownload, isQuiet: true }, downloadLabel),
                    React.createElement(DialogTrigger, null,
                        React.createElement(ActionButton, { isQuiet: true }, "Publish"),
                        React.createElement(PublishDialog, null)),
                    React.createElement(Button, { variant: "cta", marginTop: "size-250", onPress: dismiss }, "Done"))))));
};
function ImageControls() {
    const dispatch = useAppDispatch();
    const hasAnyPages = useSelector((state) => getPagesCount(state) > 0);
    const onClose = () => {
        analytics({
            action: 'ProjectAction',
            label: 'closeProject',
        });
        dispatch(EditorActions.closeProject());
    };
    if (!hasAnyPages) {
        return null;
    }
    const ShareIcon = Capabilities.shared.isAndroid ? ShareAndroid : ShareLight;
    return (React.createElement(Flex, { direction: "row", marginStart: "auto", marginEnd: "size-175", gap: "size-125" },
        React.createElement(Feedback, { type: "modal", content: React.createElement(FeedbackIcon, null), isQuiet: true },
            React.createElement(Divider, { orientation: "vertical", size: "S" })),
        React.createElement(DialogTrigger, { isDismissable: true },
            React.createElement(ActionButton, { isQuiet: true },
                React.createElement(ShareIcon, null)),
            React.createElement(ShareDialog, null)),
        React.createElement(Divider, { orientation: "vertical", size: "S" }),
        React.createElement(DialogTrigger, null,
            React.createElement(ActionButton, { isQuiet: true },
                React.createElement(Close, { size: "XS" })),
            React.createElement(AlertDialog, { variant: "destructive", title: "Close project", primaryActionLabel: "Close", cancelLabel: "Cancel", onPrimaryAction: onClose }, "This will permanently delete project and close image file. Continue?"))));
}
export function HeaderView() {
    const activeController = useSelector((state) => state.editor.activeController);
    let Controls = ImageControls;
    switch (activeController) {
        case 'image-crop':
            Controls = CropControls;
            break;
        default:
            break;
    }
    return (React.createElement(View, { backgroundColor: "gray-50", borderBottomWidth: "thick", borderBottomColor: "gray-200" },
        React.createElement(Flex, { position: "relative", direction: "row", alignItems: "center", height: "100%" },
            React.createElement(Flex, { direction: "row", marginX: "size-200", UNSAFE_className: "slogo", alignItems: "center" },
                React.createElement(LogoInHTML, null),
                React.createElement("span", { style: { display: 'none' } }, "Annotely")),
            Controls && React.createElement(Controls, null))));
}
